import { safeFetchAPI } from '@/api';
import { utils } from '@/helpers';
import { InstalledCoMarketingTemplateWithCampaign, Paginated, Pagination, SponsoredCoMarketingTemplateWithCampaign } from '@/types';
import { create } from 'zustand';

type Paginator = Pagination | ((pagination: Pagination) => Pagination);

export type SponsoredTemplatesStoreState = {
	loaded: boolean;
	loading: boolean;
	template?: SponsoredCoMarketingTemplateWithCampaign;
	templateID?: string;
	loadedInstalledTemplates: boolean;
	installedTemplates: InstalledCoMarketingTemplateWithCampaign[];
} & Pagination;

export type SponsoredTemplatesStoreActions = {
	fetchTemplate: (templateID: string) => Promise<void>;
	fetchInstalledTemplates: (templateID: string, pagination?: Paginator) => Promise<void>;

	nextPage: () => void;
	previousPage: () => void;

	reset: () => void;
};

export type SponsoredTemplatesStore = SponsoredTemplatesStoreState & SponsoredTemplatesStoreActions;

const initialState: SponsoredTemplatesStoreState = {
	loaded: false,
	loading: false,
	template: undefined,
	templateID: undefined,
	loadedInstalledTemplates: false,
	installedTemplates: [],
	page: 1,
	limit: 10,
	totalEntries: 0,
};

export const useSponsoredTemplateStore = create<SponsoredTemplatesStore>((set, get) => ({
	...initialState,
	fetchTemplate: async (templateID: string) => {
		set({ loading: true });
		const [template, error] = await safeFetchAPI<SponsoredCoMarketingTemplateWithCampaign>(`/co-marketing/sponsored/:uid/${templateID}`, {
			queryParams: {
				includeCampaign: true,
			},
		});
		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching sponsored templates',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}
		set({ template: template, loaded: true, loading: false, templateID: templateID });
	},
	fetchInstalledTemplates: async (templateID: string, pagination?: Paginator) => {
		set({ loading: true });
		const current = { page: get().page, limit: get().limit };
		const paginator = (typeof pagination === 'function' ? pagination({ page: current.page, limit: current.limit }) : pagination) ?? current;
		const [templates, error] = await safeFetchAPI<Paginated<InstalledCoMarketingTemplateWithCampaign>>(`/co-marketing/sponsored/:uid/${templateID}/installed`, {
			queryParams: {
				includeCampaign: true,
				page: paginator?.page || 1,
				limit: paginator?.limit || 10,
			},
		});
		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching sponsored templates',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}

		set({ ...paginator, installedTemplates: templates?.data ?? [], loadedInstalledTemplates: true, loading: false });
	},
	nextPage: () => {
		const totalPages = Math.ceil((get().totalEntries ?? 0) / (get().limit ?? 0));
		if (get().page < totalPages) {
			get().fetchInstalledTemplates(get().templateID ?? '', { page: get().page + 1 });
		}
	},

	previousPage: () => {
		if (get().page > 1) {
			get().fetchInstalledTemplates(get().templateID ?? '', { page: get().page - 1 });
		}
	},
	reset: () => {
		set(initialState);
	},
}));
